/**
 * Runtime half of the install offer: catching the browser's prompt and firing it.
 *
 * `beforeinstallprompt` is fired ONCE, early, shortly after load — and if
 * nobody calls `preventDefault()` on it the browser handles it its own way and
 * the chance is gone. A listener added when a banner mounts is usually too
 * late, so capture starts at app boot (main.tsx) and the event is parked here
 * until something asks for it.
 *
 * The pure rules — which platform gets which offer, and how long a dismissal
 * lasts — live in `lib/pwaInstall.ts` and are tested there.
 */

import { useEffect, useState } from 'react';

import {
  DISMISS_KEY,
  detectInstallMode,
  isDismissed,
  type InstallMode,
} from '../lib/pwaInstall';

/** The slice of BeforeInstallPromptEvent we use; it is not in lib.dom. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** True when the page is running as an installed app rather than a tab. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

function currentMode(): InstallMode {
  if (typeof window === 'undefined') return 'unsupported';
  return detectInstallMode({
    userAgent: window.navigator.userAgent,
    standalone: isStandalone(),
    hasPrompt: deferred !== null,
    maxTouchPoints: window.navigator.maxTouchPoints,
  });
}

/** Called once at app start, before anything renders. */
export function startInstallCapture(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Without this the browser shows its own mini-infobar and the event cannot
    // be replayed later from our button.
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    notify();
  });

  // Installed from anywhere — our button, the browser menu, a second tab.
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/**
 * Open the browser's install dialog. Only meaningful in `prompt` mode.
 *
 * The captured event is single-use: once prompted it cannot be replayed, so it
 * is cleared either way. A user who declines gets the card back after the
 * dismissal window, by which point the browser will have offered a new event.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = deferred;
  if (!e) return 'unavailable';
  deferred = null;
  notify();
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  }
}

/** Remember a "not now" so the card stops asking for a month. */
export function dismissInstallOffer(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, new Date().toISOString());
  } catch {
    // Private mode / blocked storage: the card simply reappears next visit,
    // which is better than failing the click.
  }
  notify();
}

function readDismissed(): boolean {
  try {
    return isDismissed(window.localStorage.getItem(DISMISS_KEY), new Date().toISOString());
  } catch {
    return false;
  }
}

export interface InstallState {
  mode: InstallMode;
  dismissed: boolean;
}

/** Live install state. Re-renders when the browser hands us a prompt. */
export function useInstallState(): InstallState {
  const [state, setState] = useState<InstallState>(() => ({
    mode: currentMode(),
    dismissed: readDismissed(),
  }));

  useEffect(() => {
    const update = () => setState({ mode: currentMode(), dismissed: readDismissed() });
    listeners.add(update);
    // The display mode changes when the app is launched from the home screen
    // in the same session.
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener('change', update);
    update();
    return () => {
      listeners.delete(update);
      mq.removeEventListener('change', update);
    };
  }, []);

  return state;
}
